import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import useSort, { applySort } from '../../hooks/useSort';
import { usePageParams, pageMeta } from '../../paging';
import { Band, Card } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import Pagination from '../../components/Pagination';
import { formatCompactNumber } from '../../format';

/**
 * Growth › Cohorts — retention, and who the signups actually are.
 *
 * The retention grid is a heatmap rather than a table of numbers because the
 * question it answers is "does the curve flatten, and in which week" — a
 * shape question that a grid of percentages makes you compute in your head.
 * The numbers stay printed on the cells so the shape never has to be trusted
 * on its own (dataviz: colour is never the only encoding).
 */
const WEEKS = 9;

function retentionTone(pct) {
  if (pct == null) return { bg: 'transparent', fg: 'var(--ink-muted, #898781)' };
  // One hue, light to dark — retention is one measure at increasing depth,
  // not several unrelated series, so a sequential ramp is correct and a
  // categorical palette would be actively misleading.
  const t = Math.min(1, pct / 60);
  const alpha = 0.08 + t * 0.62;
  return {
    bg: `rgba(42, 120, 214, ${alpha.toFixed(3)})`,
    fg: t > 0.55 ? '#ffffff' : '#0b0b0b',
  };
}

export default function CohortsPage() {
  const { page, pageSize, setPage, setPageSize } = usePageParams();
  const { sort, onSort } = useSort();

  const [data, setData] = useState(null);
  const [segments, setSegments] = useState([]);
  const [segmentMeta, setSegmentMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [segmentsLoading, setSegmentsLoading] = useState(true);

  // Retention comes from the aggregate payload; the segment table has its own
  // endpoint so turning its page does not refetch the trend, DAU, WAU/MAU and
  // retention blocks alongside it.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get('/users/aggregate', {
          params: { cohort_page_size: 1 },
        });
        setData(data);
      } catch (err) {
        toast.error(errMsg(err, 'Could not load cohorts'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadSegments = useCallback(async () => {
    setSegmentsLoading(true);
    try {
      const { data } = await adminApi.get('/users/cohorts', {
        params: { page, page_size: pageSize },
      });
      setSegments(data.items || []);
      setSegmentMeta(pageMeta(data));
    } catch (err) {
      toast.error(errMsg(err, 'Could not load signup segments'));
    } finally {
      setSegmentsLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { loadSegments(); }, [loadSegments]);

  const cohorts = data?.retention?.cohorts || [];

  const segmentColumns = useMemo(() => [
    { key: 'signup_week', label: 'Signup week', sortable: true, render: (r) => <span className="font-medium text-ink">{r.signup_week}</span> },
    { key: 'state', label: 'State', sortable: true },
    { key: 'age_band', label: 'Age band', sortable: true },
    { key: 'count', label: 'Users', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.count) },
  ], []);

  const sortedSegments = useMemo(() => applySort(segments, sort, segmentColumns), [segments, sort, segmentColumns]);

  return (
    <div>
      <PageHeader
        title="Cohorts"
        hint="Retention by signup week, and how signups break down by place and age band."
      />

      <Band title={`Retention${data?.retention?.date ? ` — as of ${data.retention.date}` : ''}`}>
        {loading ? (
          <Card><p className="text-sm text-ink-muted">Loading…</p></Card>
        ) : cohorts.length === 0 ? (
          <EmptyState
            title="Not enough signup history yet"
            detail="Retention needs at least a couple of complete weeks of signups before the curve means anything."
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-hairline bg-surface shadow-sm">
            <table className="w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-hairline text-ink-muted">
                  <th className="px-3 py-2.5 font-medium">Cohort week</th>
                  <th className="px-3 py-2.5 text-right font-medium">Size</th>
                  {Array.from({ length: WEEKS }, (_, w) => (
                    <th key={w} className="px-2 py-2.5 text-center font-medium">W{w}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {cohorts.map((c) => (
                  <tr key={c.cohort_week}>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-ink">{c.cohort_week}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-secondary">
                      {formatCompactNumber(c.cohort_size)}
                    </td>
                    {Array.from({ length: WEEKS }, (_, w) => {
                      const pct = c.retention_pct?.[String(w)];
                      const tone = retentionTone(pct);
                      return (
                        <td key={w} className="p-0.5">
                          <div
                            className="rounded px-1.5 py-1.5 text-center text-[11px] font-medium tabular-nums"
                            style={{ background: tone.bg, color: pct == null ? '#898781' : tone.fg }}
                          >
                            {pct == null ? '·' : `${pct}%`}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[11px] text-ink-muted">
          Darker is higher. W0 is the signup week itself, so it is always the largest — the number that
          matters is where the row stops falling.
        </p>
      </Band>

      <Band title="Signup segments — week × state × age band">
        <DataTable
          columns={segmentColumns}
          rows={sortedSegments}
          rowKey={(r, i) => `${r.signup_week}-${r.state}-${r.age_band}-${i}`}
          loading={segmentsLoading}
          dense
          sort={sort}
          onSort={onSort}
          empty={
            <EmptyState
              title="No cohort data yet"
              detail="Segments are built from profile state and birth year; both are collected at signup, so this fills in as people join."
            />
          }
        />
        <Pagination
          meta={segmentMeta}
          loading={segmentsLoading}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      </Band>
    </div>
  );
}
